import { createSlice } from '@reduxjs/toolkit';
import { PaginationMetaModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { resetAll } from '../../../../shared/infra/redux/baseActions';
import { ReduxStatus } from '../../../../shared/types';
import {
  AdminAnalyticsTypes,
  DataPoint,
} from '../infra/types/AnalyticsOverviewTypes';
import { AnalyticPercentageModel } from '../models/AnalyticPercentage.model';
import { CommunitiesOverviewModel } from '../models/CommunitiesOverview.model';
import { CommunityFilteredAnalyticsModel } from '../models/CommunityFilteredAnalytics.model';
import { FilteredAnalyticsModel } from '../models/FilteredAnalytics.model';
import {
  getCommunitiesOverview,
  getAnalyticsOverview,
  getCommunityPercentages,
  getFilteredAnalytics,
  getMultilineAnalytics,
  getOpenFilteredAnalytics,
} from './operators';

export interface AdminAnalyticsState {
  overviews: CommunitiesOverviewModel[];
  pagination: PaginationMetaModel;
  getCommunitiesOverviewStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  communityPercentages: {
    data: AnalyticPercentageModel;
    getCommunityPercentagesStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
  communityFilteredAnalytics: {
    data: CommunityFilteredAnalyticsModel[];
    meta: PaginationMetaModel;
    getCommunityAnalyticsStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
  filteredAnalytics: {
    data: FilteredAnalyticsModel[];
    getFilteredAnalyticsStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
  registeredAnalytics: {
    [AdminAnalyticsTypes.ALL]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.MONTH]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.LIFETIME]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.QUARTER]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.RANGE]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.WEEK]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.YEAR]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.CUSTOM]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
  };
  unregisteredAnalytics: {
    [AdminAnalyticsTypes.ALL]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.MONTH]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.LIFETIME]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.QUARTER]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.RANGE]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.WEEK]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.YEAR]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
    [AdminAnalyticsTypes.CUSTOM]: {
      overview: DataPoint[];
      getOverviewStatus: {
        status: ReduxStatus;
        error: string | null;
      };
    };
  };
}

const defualtOverview: DataPoint[] = [
  {
    label: 'Communities',
    value: 0,
  },
  {
    label: 'Users',
    value: 0,
  },
  {
    label: 'Views',
    value: 0,
  },
  {
    label: 'Conversions',
    value: 0,
  },
  {
    label: 'Published Content',
    value: 0,
  },
  {
    label: 'Sent Notificaiton',
    value: 0,
  },
];

const defaultPercentages: AnalyticPercentageModel = {
  CONTENT: [],
  NOTIFICATION: [],
  USER: [],
};

const initialState: AdminAnalyticsState = {
  overviews: [],
  pagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  getCommunitiesOverviewStatus: {
    status: 'idle',
    error: null,
  },
  communityPercentages: {
    data: defaultPercentages,
    getCommunityPercentagesStatus: {
      status: 'idle',
      error: null,
    },
  },
  communityFilteredAnalytics: {
    data: [],
    meta: {
      current: 0,
      pages: 0,
      total: 0,
    },
    getCommunityAnalyticsStatus: {
      status: 'idle',
      error: null,
    },
  },
  filteredAnalytics: {
    data: [],
    getFilteredAnalyticsStatus: {
      status: 'idle',
      error: null,
    },
  },
  registeredAnalytics: {
    [AdminAnalyticsTypes.ALL]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.MONTH]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.LIFETIME]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.QUARTER]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.RANGE]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.WEEK]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.YEAR]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.CUSTOM]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
  },
  unregisteredAnalytics: {
    [AdminAnalyticsTypes.ALL]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.MONTH]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.LIFETIME]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.QUARTER]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.RANGE]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.WEEK]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.YEAR]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
    [AdminAnalyticsTypes.CUSTOM]: {
      overview: defualtOverview,
      getOverviewStatus: {
        status: 'idle',
        error: null,
      },
    },
  },
};

const adminAnalyticsSlice = createSlice({
  name: 'adminAnalytics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getMultilineAnalytics.pending, (state) => {
      state.filteredAnalytics.getFilteredAnalyticsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getMultilineAnalytics.fulfilled, (state, { payload }) => {
      state.filteredAnalytics.getFilteredAnalyticsStatus = {
        status: 'success',
        error: null,
      };
      state.filteredAnalytics.data = payload;
    });
    builder.addCase(getMultilineAnalytics.rejected, (state, { payload }) => {
      state.filteredAnalytics.getFilteredAnalyticsStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch filtered analytics',
      };
    });
    builder.addCase(getCommunityPercentages.pending, (state) => {
      state.communityPercentages.getCommunityPercentagesStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getCommunityPercentages.fulfilled, (state, { payload }) => {
      state.communityPercentages.getCommunityPercentagesStatus = {
        status: 'success',
        error: null,
      };
      state.communityPercentages.data = payload;
    });
    builder.addCase(getCommunityPercentages.rejected, (state, { payload }) => {
      state.communityPercentages.getCommunityPercentagesStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch community percentages',
      };
    });
    builder.addCase(getCommunitiesOverview.pending, (state) => {
      state.getCommunitiesOverviewStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getCommunitiesOverview.fulfilled, (state, { payload }) => {
      state.getCommunitiesOverviewStatus = {
        status: 'success',
        error: null,
      };
      state.overviews = payload.model;
      state.pagination = payload.meta;
    });
    builder.addCase(getCommunitiesOverview.rejected, (state, { payload }) => {
      state.getCommunitiesOverviewStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch communities overview',
      };
    });
    builder.addCase(getFilteredAnalytics.pending, (state, { meta }) => {
      const { arg } = meta;
      state.registeredAnalytics[arg.filter].getOverviewStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(
      getFilteredAnalytics.fulfilled,
      (state, { payload, meta }) => {
        const { arg } = meta;
        state.registeredAnalytics[arg.filter].getOverviewStatus = {
          status: 'success',
          error: null,
        };
        state.registeredAnalytics[arg.filter].overview = payload;
      },
    );
    builder.addCase(
      getFilteredAnalytics.rejected,
      (state, { meta, payload }) => {
        const { arg } = meta;
        state.registeredAnalytics[arg.filter].getOverviewStatus = {
          status: 'failure',
          error: payload?.message
            ? payload.message
            : `Error trying to pull ${arg.filter} filterd analytics`,
        };
      },
    );
    builder.addCase(getOpenFilteredAnalytics.pending, (state, { meta }) => {
      const { arg } = meta;
      state.unregisteredAnalytics[arg.filter].getOverviewStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(
      getOpenFilteredAnalytics.fulfilled,
      (state, { meta, payload }) => {
        const { arg } = meta;
        state.unregisteredAnalytics[arg.filter].getOverviewStatus = {
          status: 'success',
          error: null,
        };
        state.unregisteredAnalytics[arg.filter].overview = payload;
      },
    );
    builder.addCase(
      getOpenFilteredAnalytics.rejected,
      (state, { meta, payload }) => {
        const { arg } = meta;
        state.unregisteredAnalytics[arg.filter].getOverviewStatus = {
          status: 'failure',
          error: payload?.message
            ? payload?.message
            : `Error trying to pull ${arg.filter} filtered analytics`,
        };
      },
    );
    builder.addCase(getAnalyticsOverview.pending, (state, { meta }) => {
      const { arg } = meta;
      state.registeredAnalytics[arg.filter].getOverviewStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(
      getAnalyticsOverview.fulfilled,
      (state, { payload, meta }) => {
        const { arg } = meta;
        state.registeredAnalytics[arg.filter].getOverviewStatus = {
          status: 'success',
          error: null,
        };
        state.registeredAnalytics[arg.filter].overview = payload;
      },
    );
    builder.addCase(
      getAnalyticsOverview.rejected,
      (state, { payload, meta }) => {
        const { arg } = meta;
        state.registeredAnalytics[arg.filter].getOverviewStatus = {
          status: 'failure',
          error: payload?.message
            ? payload.message
            : `Error trying to pull ${arg.filter} overview analytics`,
        };
      },
    );
    builder.addCase(resetAll, () => initialState);
  },
});

export default adminAnalyticsSlice.reducer;
